import React, { useState, useMemo } from 'react';
import { View, Text, ScrollView, Pressable, TextInput } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { useAuth, supabase, isSupabaseConfigured } from '@/lib/supabase';
import { buildStartupProfileUpdate } from '@/lib/supabase/profile-mappers';
import { handleError, reportError } from '@/lib/errors';
import AnimatedPressable from '@/components/AnimatedPressable';
import {
  Building2,
  MapPin,
  Calendar,
  Users,
  Globe,
  DollarSign,
  Target,
  Settings,
  LogOut,
  ChevronRight,
  Edit3,
  Crown,
  Gift,
  UserPlus,
  ChevronLeft,
  Sparkles,
  FileText,
  ExternalLink,
  TrendingUp,
  AlertCircle,
  Shield,
  CheckCircle,
  XCircle,
  Clock,
  Star,
  Rocket,
  Megaphone,
  Newspaper,
  Lock,
  FolderOpen,
  Save,
  X,
  Linkedin,
  Lightbulb,
  BarChart3,
  Briefcase,
} from 'lucide-react-native';
import Animated, { FadeInDown, FadeInUp } from 'react-native-reanimated';
import { track, useTrackScreen } from '@/lib/analytics';
import { useThemeColors } from '@/lib/useThemeColors';
import { useInverioStore } from '@/lib/state/inverio-store';
import { useProfileStore } from '@/lib/state/profile-store';
import type { VerificationRequest, StartupUpdate, UpdateVisibility, FundraisingRound } from '@/lib/state/profile-store';
import { useDealflowStore } from '@/lib/state/dealflow-store';
import type { ExternalCommitment } from '@/lib/state/dealflow-store';
import { useShallow } from 'zustand/react/shallow';
import { formatCurrency } from '@/lib/utils/format';
import { FundraisingSection } from '@/components/startup/FundraisingSection';
import { capitalizeWords } from '@/lib/format';
import { isWeb } from '@/lib/platform';
import { usePhoneShell } from '@/lib/useShell';
import { AppLayout } from '@/components/AppLayout';
import { useAlert } from '@/components/AlertDialog';
import { SectionDivider } from '@/components/SectionDivider';
import RecentUpdates from '@/components/RecentUpdates';
import { mode } from '@/lib/config/app-mode';
import { useFounderStartup } from '@/lib/interview/use-founder-startup';
import { useLatestSessionDigest } from '@/lib/interview/use-interview';
import { InterviewDigestReview } from '@/components/interview/InterviewDigestReview';
import { useFlag } from '@/lib/feature-flags';
import { StrategicAnalysisEntry } from '@/components/strategic-analysis/StrategicAnalysisEntry';
import { useStrategicEntryState } from '@/lib/strategic-analysis/use-strategic-analysis';
import { KeyboardAwareBody } from '@/components/modal/KeyboardAwareBody';
import { CompanyLogoEditor } from '@/components/company/CompanyLogoEditor';
import { useMyCompanyLogo } from '@/lib/company/use-set-company-logo';
import { COMPANY_PLANS_LINK } from '@/lib/commerce/navigation';

// Standard shadow styles
const cardShadow = {
  shadowColor: '#000',
  shadowOpacity: 0.05,
  shadowRadius: 8,
  shadowOffset: { width: 0, height: 2 },
  elevation: 2,
};

// Mock company description - AI-extracted from data room
const COMPANY_DESCRIPTION = {
  summary: "TechFlow AI is pioneering the future of enterprise automation through advanced machine learning and natural language processing. Founded in 2022 by a team of Stanford AI researchers and former Google engineers, the company has developed a proprietary algorithm that reduces manual data processing by 85% while maintaining 99.2% accuracy.",
  mission: "Our mission is to democratize AI-powered automation for mid-market businesses, enabling them to compete with enterprise-level efficiency without enterprise-level budgets.",
  problem: "Mid-market companies spend an average of $2.3M annually on manual data processing and document handling. Traditional automation solutions require 6-12 months of implementation and $500K+ in consulting fees, putting them out of reach for most SMBs.",
  solution: "TechFlow's plug-and-play AI platform integrates with existing systems in under 2 weeks, delivering immediate ROI through intelligent document processing, automated workflows, and predictive analytics. Our customers see an average 40% reduction in operational costs within the first quarter.",
  traction: "Since launching in Q3 2023, we've onboarded 47 paying customers with $890K ARR, achieved 94% customer retention, and processed over 2.1M documents with zero critical errors.",
  lastUpdated: "2 days ago",
  sources: ["Pitch Deck v2.pdf", "Executive Summary.docx", "Financial Model.xlsx"],
};

// Mock AI-extracted insights from data room
const AI_INSIGHTS = [
  {
    id: '1',
    category: 'Market Position',
    insight: 'Based on your pitch deck, you are targeting the $5.2B SMB fintech market with a 15% projected CAGR through 2028.',
    source: 'Pitch Deck v2.pdf',
    sourcePage: 12,
    confidence: 0.92,
    updatedAt: '2 hours ago',
  },
  {
    id: '2',
    category: 'Financial Metrics',
    insight: 'Your financial model shows projected ARR of $2.4M by Q4 2025 with 85% gross margins.',
    source: 'Financial Model.xlsx',
    sourcePage: null,
    confidence: 0.88,
    updatedAt: '1 day ago',
  },
  {
    id: '3',
    category: 'Competitive Advantage',
    insight: 'Key differentiators include proprietary ML algorithm (mentioned 4x in pitch) and 40% faster onboarding than competitors.',
    source: 'Pitch Deck v2.pdf',
    sourcePage: 8,
    confidence: 0.85,
    updatedAt: '3 days ago',
  },
  {
    id: '4',
    category: 'Risk Factor',
    insight: 'Customer concentration risk detected: top 3 customers represent 65% of revenue based on cap table.',
    source: 'Cap Table.xlsx',
    sourcePage: null,
    confidence: 0.78,
    updatedAt: '1 week ago',
  },
];

const stageLabels: Record<string, string> = {
  'pre-seed': 'Pre-Seed',
  'seed': 'Seed',
  'series-a': 'Series A',
};

// Verification request status config
const getStatusConfig = (colors: ReturnType<typeof useThemeColors>): Record<
  VerificationRequest['status'],
  { label: string; icon: typeof Clock; color: string; bgColor: string }
> => ({
  pending: {
    label: 'Pending',
    icon: Clock,
    color: colors.amber.main,
    bgColor: colors.amber.light,
  },
  verified: {
    label: 'Verified',
    icon: CheckCircle,
    color: colors.traffic.green,
    bgColor: colors.traffic.greenLight,
  },
  rejected: {
    label: 'Rejected',
    icon: XCircle,
    color: colors.traffic.red,
    bgColor: colors.traffic.redLight,
  },
});

// Update type config
type UpdateType = StartupUpdate['type'];

export default function StartupProfileScreen() {
  useTrackScreen('Company');
  const router = useRouter();
  const { signOut } = useAuth();
  const colors = useThemeColors();
  const phoneShell = usePhoneShell();
  // Profile store — shallow selectors for non-primitives
  const profile = useProfileStore(useShallow((s) => s.startupProfile));
  // The logo lives on company_directory, not startup_profiles — it is the only
  // table that has ever held one. Provenance comes with it so the founder can
  // be told when what they are looking at is our guess.
  const { data: companyLogo } = useMyCompanyLogo(profile?.id);
  const setStartupProfile = useProfileStore((s) => s.setStartupProfile);
  const reset = useInverioStore((s) => s.reset);
  const teamMembers = useProfileStore(useShallow((s) => s.teamMembers));
  const verificationRequests = useProfileStore(useShallow((s) => s.verificationRequests));
  const respondToVerification = useProfileStore((s) => s.respondToVerification);

  // Dealflow store — shallow selectors for non-primitives
  const commitments = useDealflowStore(useShallow((s) => s.commitments));
  const externalCommitments = useDealflowStore(useShallow((s) => s.externalCommitments));
  const commitmentSettings = useDealflowStore(useShallow((s) => s.commitmentSettings));
  const setCommitmentSettings = useDealflowStore((s) => s.setCommitmentSettings);
  const addExternalCommitment = useDealflowStore((s) => s.addExternalCommitment);
  const updateExternalCommitment = useDealflowStore((s) => s.updateExternalCommitment);
  const removeExternalCommitment = useDealflowStore((s) => s.removeExternalCommitment);
  const getCommitmentSummary = useDealflowStore((s) => s.getCommitmentSummary);
  const closeFundraisingRound = useDealflowStore((s) => s.closeFundraisingRound);
  const { confirmDestructive, alert, show: showAlert } = useAlert();

  // Status config for verification requests
  const STATUS_CONFIG = getStatusConfig(colors);
  const pendingRequests = verificationRequests.filter((r) => r.status === 'pending');
  const updates = profile?.updates || [];

  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Re-open the stored interview digest to review + selectively publish later,
  // without re-interviewing (Task 2.3). Resolve the REAL startup id from the DB
  // (the store id may be a simulated onboarding value), then read the latest
  // persisted digest for it.
  const { startupId: realStartupId } = useFounderStartup();
  const { data: latestDigest } = useLatestSessionDigest(realStartupId ?? undefined);
  // One kill switch, defaulting closed. The server enforces it again; this only
  // decides whether a card renders.
  const strategicAnalysisEnabled = useFlag('strategic_analyses', { default: false });
  // Spread onto the card as one object. Passing these field by field is how the
  // card ended up frozen at `not_generated` with a literal `hasReport={false}`.
  const strategicEntry = useStrategicEntryState(realStartupId ?? undefined, {
    canEdit: true,
    enabled: strategicAnalysisEnabled,
  });
  const [digestReviewOpen, setDigestReviewOpen] = useState(false);

  // External commitment form state
  const [showAddExternal, setShowAddExternal] = useState(false);
  const [editingExternalId, setEditingExternalId] = useState<string | null>(null);
  const [extName, setExtName] = useState('');
  const [extFirm, setExtFirm] = useState('');
  const [extAmount, setExtAmount] = useState('');
  const [extType, setExtType] = useState<'soft' | 'hard'>('soft');
  const [extNotes, setExtNotes] = useState('');

  // Form state for editing
  const [formData, setFormData] = useState({
    name: profile?.name || '',
    tagline: profile?.tagline || '',
    description: profile?.description || '',
    industry: profile?.industry || '',
    stage: profile?.stage || 'seed',
    location: profile?.location || '',
    foundedYear: profile?.foundedYear?.toString() || '',
    teamSize: profile?.teamSize?.toString() || '',
    website: profile?.website || '',
    linkedIn: profile?.linkedIn || '',
    fundingTarget: profile?.fundingTarget?.toString() || '',
    minimumTicket: profile?.minimumTicket?.toString() || '',
    problem: profile?.problem || '',
    solution: profile?.solution || '',
    targetCustomer: profile?.targetCustomer || '',
    monthlyRevenue: profile?.monthlyRevenue?.toString() || '',
    userCount: profile?.userCount?.toString() || '',
    growthRate: profile?.growthRate?.toString() || '',
    marketSize: profile?.marketSize?.toString() || '',
  });

  // Edit field styling
  const editFieldStyle = {
    backgroundColor: colors.primary[50],
    borderWidth: 2,
    borderColor: colors.primary[300],
    borderStyle: 'dashed' as const,
  };

  const viewFieldStyle = {
    backgroundColor: colors.surface.secondary,
    borderWidth: 1,
    borderColor: colors.border.light,
  };

  const startEditing = () => {
    // Reset form data to current profile values
    setFormData({
      name: profile?.name || '',
      tagline: profile?.tagline || '',
      description: profile?.description || '',
      industry: profile?.industry || '',
      stage: profile?.stage || 'seed',
      location: profile?.location || '',
      foundedYear: profile?.foundedYear?.toString() || '',
      teamSize: profile?.teamSize?.toString() || '',
      website: profile?.website || '',
      linkedIn: profile?.linkedIn || '',
      fundingTarget: profile?.fundingTarget?.toString() || '',
      minimumTicket: profile?.minimumTicket?.toString() || '',
      problem: profile?.problem || '',
      solution: profile?.solution || '',
      targetCustomer: profile?.targetCustomer || '',
      monthlyRevenue: profile?.monthlyRevenue?.toString() || '',
      userCount: profile?.userCount?.toString() || '',
      growthRate: profile?.growthRate?.toString() || '',
      marketSize: profile?.marketSize?.toString() || '',
    });
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
  };

  const saveChanges = async () => {
    if (!profile) return;

    if (!formData.name.trim()) {
      alert('Required', 'Company name is required');
      return;
    }

    setIsSaving(true);

    const updatedProfile = {
      ...profile,
      name: formData.name.trim(),
      tagline: formData.tagline.trim(),
      description: formData.description.trim() || undefined,
      industry: formData.industry.trim(),
      stage: formData.stage as 'pre-seed' | 'seed' | 'series-a',
      location: formData.location.trim(),
      foundedYear: parseInt(formData.foundedYear) || profile.foundedYear,
      teamSize: parseInt(formData.teamSize) || profile.teamSize,
      website: formData.website.trim() || undefined,
      linkedIn: formData.linkedIn.trim() || undefined,
      fundingTarget: parseInt(formData.fundingTarget) || profile.fundingTarget,
      minimumTicket: formData.minimumTicket ? parseInt(formData.minimumTicket) : undefined,
      problem: formData.problem.trim() || undefined,
      solution: formData.solution.trim() || undefined,
      targetCustomer: formData.targetCustomer.trim() || undefined,
      monthlyRevenue: formData.monthlyRevenue ? parseInt(formData.monthlyRevenue) : undefined,
      userCount: formData.userCount ? parseInt(formData.userCount) : undefined,
      growthRate: formData.growthRate ? parseFloat(formData.growthRate) : undefined,
      marketSize: formData.marketSize ? parseInt(formData.marketSize) : undefined,
    };

    try {
      // Real persistence — owner/member UPDATE governed by the Task-0.2
      // "team members can update" RLS policy, keyed by profile.id (not
      // user_id). isSupabaseConfigured() gate matches the rest of the app's
      // mock-vs-live separation: mock mode updates the store only.
      if (isSupabaseConfigured()) {
        const { error } = await supabase
          .from('startup_profiles')
          .update(buildStartupProfileUpdate(updatedProfile))
          .eq('id', profile.id);

        if (error) throw error;
      }

      setStartupProfile(updatedProfile);
      track('profile_updated', { section: 'company' });
      setIsEditing(false);
    } catch (err) {
      const { message, isCancelled } = handleError(err, { context: 'company.save' });
      if (!isCancelled) alert('Save Failed', message);
    } finally {
      setIsSaving(false);
    }
  };

  const updateField = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Commitment computed values
  const commitmentSummary = profile ? getCommitmentSummary(profile.id, profile) : null;
  const myVerifiedCommitments = commitments.filter(
    c => c.startupId === profile?.id && (c.status === 'active' || c.status === 'converted')
  );
  const myExternalCommitments = externalCommitments.filter(c => c.startupId === profile?.id);

  // External commitment handlers
  const handleAddExternal = () => {
    if (!profile || !extName.trim() || !extAmount.trim()) return;
    const amount = parseFloat(extAmount.replace(/[^0-9.]/g, ''));
    if (isNaN(amount) || amount <= 0) return;

    if (editingExternalId) {
      updateExternalCommitment(editingExternalId, {
        investorName: extName.trim(),
        investorFirm: extFirm.trim() || undefined,
        amount,
        type: extType,
        notes: extNotes.trim() || undefined,
      });
      setEditingExternalId(null);
    } else {
      addExternalCommitment({
        id: `ec_${Date.now()}`,
        startupId: profile.id,
        investorName: extName.trim(),
        investorFirm: extFirm.trim() || undefined,
        type: extType,
        source: 'unverified',
        amount,
        status: 'active',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        notes: extNotes.trim() || undefined,
      });
    }

    // Reset form
    setExtName('');
    setExtFirm('');
    setExtAmount('');
    setExtType('soft');
    setExtNotes('');
    setShowAddExternal(false);
  };

  const handleEditExternal = (commitment: ExternalCommitment) => {
    setExtName(commitment.investorName);
    setExtFirm(commitment.investorFirm ?? '');
    setExtAmount(commitment.amount.toString());
    setExtType(commitment.type);
    setExtNotes(commitment.notes ?? '');
    setEditingExternalId(commitment.id);
    setShowAddExternal(true);
  };

  // Close round handler
  const handleCloseRound = async () => {
    if (!profile || !commitmentSummary) return;

    const summaryText = [
      `Total raised: ${formatCurrency(commitmentSummary.totalCommitted)}`,
      `Verified: ${formatCurrency(commitmentSummary.totalVerifiedCommitted)} / Unverified: ${formatCurrency(commitmentSummary.totalUnverifiedCommitted)}`,
      `${commitmentSummary.verifiedInvestorCount + commitmentSummary.unverifiedInvestorCount} investor(s)`,
    ].join('\n');

    const result = await showAlert({
      title: 'Close Fundraising Round',
      message: `${summaryText}\n\nHow would you like to close this round?`,
      type: 'confirm',
      buttons: [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Cancel Round', style: 'destructive' },
        { text: 'Round Complete', style: 'default' },
      ],
    });

    if (result === 2) {
      closeFundraisingRound('completed', profile, setStartupProfile);
      track('fundraising_round_closed', { outcome: 'completed' });
    } else if (result === 1) {
      closeFundraisingRound('cancelled', profile, setStartupProfile);
      track('fundraising_round_closed', { outcome: 'cancelled' });
    }
  };

  // Helper functions for verification requests
  const handleVerify = (requestId: string) => {
    respondToVerification(requestId, 'verified');
  };

  const handleReject = (requestId: string) => {
    respondToVerification(requestId, 'rejected');
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Helper functions for updates
  const getUpdateIcon = (type: UpdateType, size = 16) => {
    switch (type) {
      case 'milestone':
        return <Star size={size} color={colors.success} />;
      case 'funding':
        return <DollarSign size={size} color={colors.success} />;
      case 'traction':
        return <TrendingUp size={size} color={colors.warning} />;
      case 'team':
        return <UserPlus size={size} color={colors.primary[600]} />;
      case 'product':
        return <Rocket size={size} color={colors.primary[600]} />;
      case 'announcement':
        return <Megaphone size={size} color={colors.primary[600]} />;
      default:
        return <Newspaper size={size} color={colors.text.tertiary} />;
    }
  };

  const getUpdateBgColor = (type: UpdateType) => {
    switch (type) {
      case 'milestone':
      case 'funding':
        return colors.traffic.greenLight;
      case 'traction':
        return colors.amber.light;
      default:
        return colors.primary[50];
    }
  };

  const getVisibilityIcon = (visibility: UpdateVisibility, size = 12) => {
    switch (visibility) {
      case 'public':
        return <Globe size={size} color={colors.text.tertiary} />;
      case 'data_room':
        return <FolderOpen size={size} color={colors.warning} />;
      case 'investors_only':
        return <Lock size={size} color={colors.primary[600]} />;
    }
  };

  const handleLogout = async () => {
    const confirmed = await confirmDestructive(
      'Sign Out',
      'Are you sure you want to sign out?',
      'Sign Out'
    );
    if (!confirmed) return;
    try {
      // Single source of truth: revokes the Supabase session + resets all
      // user-scoped stores. Without this the session survives and the user is
      // bounced straight back into the app (the native sign-out bug, 2026-06-13).
      await signOut();
      reset(); // belt-and-suspenders store reset
    } catch (error) {
      reportError(error, { context: 'StartupCompany.handleLogout' });
    } finally {
      router.replace('/login');
    }
  };

  // REMOVED: an `EditableField` component declared here, with zero call sites.
  //
  // It carried the same defect that made Location and LinkedIn untypeable on
  // /edit-profile: a component declared in a render body is a new type every
  // render, so React remounts its TextInput on each keystroke and the keyboard
  // drops. Dead code, so no user ever hit it — but it was a working example
  // sitting in the file for whoever wired up the next field.
  //
  // This screen's inline `isEditing` inputs are written directly in JSX below,
  // which is why they behave. When this screen moves to per-section editing,
  // use `components/profile/ProfileSectionFields.tsx`.

  // Profile completeness calculation for "Enhance Profile" section
  const enhanceCards = useMemo(() => {
    if (!profile) return [];

    const cards: Array<{
      id: string;
      icon: React.ReactNode;
      title: string;
      description: string;
      done: boolean;
      route: string;
      routeParams: Record<string, string>;
    }> = [
      {
        id: 'problem',
        icon: <Lightbulb size={20} color={colors.primary[600]} />,
        title: 'Add problem & solution',
        description: 'Describe the problem you solve and your approach',
        done: !!(profile.problem && profile.solution),
        route: '/(onboarding)/questionnaire-problem',
        routeParams: { mode: 'standalone' },
      },
      {
        id: 'traction',
        icon: <TrendingUp size={20} color={colors.primary[600]} />,
        title: 'Add traction metrics',
        description: 'Revenue, users, and growth rate',
        done: !!(profile.monthlyRevenue || profile.userCount || profile.growthRate),
        route: '/(onboarding)/questionnaire-traction',
        routeParams: { mode: 'standalone' },
      },
      {
        id: 'market',
        icon: <Globe size={20} color={colors.primary[600]} />,
        title: 'Add market info',
        description: 'Market size, funding target, competitors',
        done: !!(profile.marketSize || profile.competitors?.length),
        route: '/(onboarding)/questionnaire-market',
        routeParams: { mode: 'standalone' },
      },
      {
        id: 'fundraising',
        icon: <DollarSign size={20} color={colors.primary[600]} />,
        title: 'Add fundraising details',
        description: 'Round status, target amount, history',
        done: !!(profile.isActivelyRaising || profile.fundraisingHistory?.length),
        route: '/(onboarding)/fundraising-status',
        routeParams: { mode: 'standalone' },
      },
      {
        id: 'serial',
        icon: <Briefcase size={20} color={colors.primary[600]} />,
        title: 'Add previous ventures',
        description: 'Claim serial founder status',
        done: !!(profile.isSerialFounder || profile.previousVentures?.length),
        route: '/(onboarding)/serial-founder',
        routeParams: { mode: 'standalone' },
      },
    ];

    return cards;
  }, [profile, colors]);

  const profileCompleteness = useMemo(() => {
    if (enhanceCards.length === 0) return 100;
    const doneCount = enhanceCards.filter((c) => c.done).length;
    // Base: 4 essential fields always filled (name, industry, stage, location) = 40%
    // Enhancement cards = 60%
    const base = 40;
    const enhancePortion = Math.round((doneCount / enhanceCards.length) * 60);
    return base + enhancePortion;
  }, [enhanceCards]);

  const incompleteCards = enhanceCards.filter((c) => !c.done);
  const showEnhanceSection = incompleteCards.length > 0;

  if (!profile) {
    return (
      <View className="flex-1 items-center justify-center" style={{ backgroundColor: colors.surface.secondary }}>
        <Text style={{ color: colors.text.primary }}>No profile found</Text>
      </View>
    );
  }

  return (
    <AppLayout title="Company Profile">
      <View className="flex-1" style={{ backgroundColor: colors.surface.secondary }}>
        <SafeAreaView edges={isWeb ? [] : ['top']} className="flex-1">
          <KeyboardAwareBody className="flex-1" showsVerticalScrollIndicator={false}>
            {/* Header - Only show on mobile */}
            {phoneShell && (
              <Animated.View
                entering={FadeInDown.delay(100).duration(500)}
                className="px-5 pt-4 pb-2"
              >
                <View className="flex-row items-center justify-between">
                  <View className="flex-row items-center gap-3 flex-1 mr-3">
                    {/* Pushed within the tabs navigator with `href: null` — the
                        only way out was the tab bar. Same inline back control
                        every other custom mobile header in this app already
                        uses (see settings.tsx), guarded so it never renders a
                        dead control when there is no history to go back to. */}
                    {router.canGoBack() && (
                      <AnimatedPressable
                        onPress={() => router.back()}
                        accessibilityRole="button"
                        accessibilityLabel="Go back"
                        className="w-11 h-11 rounded-full items-center justify-center"
                        style={{ backgroundColor: colors.light[300] }}
                      >
                        <ChevronLeft size={20} color={colors.text.secondary} />
                      </AnimatedPressable>
                    )}
                    <View>
                      <Text style={{ color: colors.text.primary }} className="text-2xl font-bold">Company Profile</Text>
                      <Text style={{ color: colors.text.tertiary }} className="text-sm">Visible to investors</Text>
                    </View>
                  </View>
                  {!isEditing ? (
                    <AnimatedPressable
                      onPress={startEditing}
                      accessibilityRole="button"
                      accessibilityLabel="Edit company profile"
                      className="w-10 h-10 rounded-full items-center justify-center"
                      style={{ backgroundColor: colors.light[300] }}
                    >
                      <Edit3 size={18} color={colors.text.secondary} />
                    </AnimatedPressable>
                  ) : (
                    <View className="flex-row gap-2">
                      <AnimatedPressable
                        onPress={cancelEditing}
                        accessibilityRole="button"
                        accessibilityLabel="Cancel editing"
                        className="w-10 h-10 rounded-full items-center justify-center"
                        style={{ backgroundColor: colors.red.light }}
                      >
                        <X size={18} color={colors.error} />
                      </AnimatedPressable>
                      <AnimatedPressable
                        onPress={saveChanges}
                        disabled={isSaving}
                        accessibilityRole="button"
                        accessibilityLabel="Save changes"
                        accessibilityState={{ disabled: isSaving, busy: isSaving }}
                        className="w-10 h-10 rounded-full items-center justify-center"
                        style={{ backgroundColor: colors.traffic.green }}
                      >
                        <Save size={18} color="white" />
                      </AnimatedPressable>
                    </View>
                  )}
                </View>
              </Animated.View>
            )}

            {/* Web Edit/Save Header */}
            {isWeb && isEditing && (
              <View
                className="mx-5 mb-4 p-4 rounded-2xl flex-row items-center justify-between"
                style={{ backgroundColor: colors.primary[50], borderWidth: 1, borderColor: colors.primary[200] }}
              >
                <View className="flex-row items-center gap-2">
                  <Edit3 size={18} color={colors.primary[600]} />
                  <Text style={{ color: colors.primary[700] }} className="font-semibold">
                    Editing Profile
                  </Text>
                </View>
                <View className="flex-row gap-2">
                  <Pressable
                    onPress={cancelEditing}
                    className="px-4 py-2 rounded-lg active:opacity-70"
                    style={{ backgroundColor: colors.surface.primary }}
                  >
                    <Text style={{ color: colors.text.secondary }} className="font-medium">Cancel</Text>
                  </Pressable>
                  <Pressable
                    onPress={saveChanges}
                    disabled={isSaving}
                    className="px-4 py-2 rounded-lg active:opacity-70"
                    style={{ backgroundColor: colors.primary[600] }}
                  >
                    <Text className="font-medium text-white">{isSaving ? 'Saving...' : 'Save Changes'}</Text>
                  </Pressable>
                </View>
              </View>
            )}

            {/* Profile Card */}
            <Animated.View
              entering={FadeInUp.delay(200).duration(500)}
              className="mx-5 mb-6"
            >
              <View
                className="rounded-3xl p-6"
                style={{
                  backgroundColor: colors.surface.primary,
                  shadowColor: '#000',
                  shadowOffset: { width: 0, height: 2 },
                  shadowOpacity: 0.08,
                  shadowRadius: 12,
                  elevation: 3,
                }}
              >
                {/* Edit button for web (when not editing) */}
                {isWeb && !isEditing && (
                  <Pressable
                    onPress={startEditing}
                    accessibilityRole="button"
                    accessibilityLabel="Edit company profile"
                    className="absolute top-4 right-4 w-10 h-10 rounded-full items-center justify-center active:opacity-70 z-10"
                    style={{ backgroundColor: colors.light[300] }}
                  >
                    <Edit3 size={18} color={colors.text.secondary} />
                  </Pressable>
                )}

                <View className="items-center">
                  {/* Was a hardcoded initials circle. A founder had no way to set
                      their own logo — every logo in the product was extracted or
                      scraped, and `logo_source='founder'` was a tier nothing
                      could write to. */}
                  <CompanyLogoEditor
                    startupId={profile.id ?? null}
                    companyName={isEditing ? formData.name : profile.name}
                    logoUrl={companyLogo?.logo_url}
                    logoSource={(companyLogo?.logo_source as 'founder' | 'deck' | 'website' | null) ?? null}
                    isKnockout={companyLogo?.logo_is_knockout === true}
                    isEditing={isEditing}
                  />

                  {isEditing ? (
                    <View className="w-full gap-3">
                      <TextInput
                        value={formData.name}
                        onChangeText={(v) => updateField('name', v)}
                        placeholder="Company Name"
                        placeholderTextColor={colors.text.tertiary}
                        className="text-center text-2xl font-bold rounded-xl px-4 py-3"
                        style={{
                          ...editFieldStyle,
                          color: colors.text.primary,
                        }}
                      />
                      <TextInput
                        value={formData.tagline}
                        onChangeText={(v) => updateField('tagline', v)}
                        placeholder="Tagline"
                        placeholderTextColor={colors.text.tertiary}
                        className="text-center rounded-xl px-4 py-3"
                        style={{
                          ...editFieldStyle,
                          color: colors.text.secondary,
                        }}
                      />
                      <TextInput
                        value={formData.industry}
                        onChangeText={(v) => updateField('industry', v)}
                        placeholder="Industry"
                        placeholderTextColor={colors.text.tertiary}
                        className="text-center rounded-xl px-4 py-3"
                        style={{
                          ...editFieldStyle,
                          color: colors.text.primary,
                        }}
                      />
                      <View className="flex-row justify-center gap-2 mt-2">
                        {(['pre-seed', 'seed', 'series-a'] as const).map((stage) => (
                          <Pressable
                            key={stage}
                            onPress={() => updateField('stage', stage)}
                            className="px-4 py-2 rounded-full"
                            style={{
                              backgroundColor: formData.stage === stage
                                ? colors.primary[600]
                                : colors.surface.secondary,
                              borderWidth: 1,
                              borderColor: formData.stage === stage ? colors.primary[600] : colors.border.light,
                            }}
                          >
                            <Text
                              style={{
                                color: formData.stage === stage ? 'white' : colors.text.primary,
                              }}
                              className="text-sm font-medium"
                            >
                              {stageLabels[stage]}
                            </Text>
                          </Pressable>
                        ))}
                      </View>
                    </View>
                  ) : (
                    <>
                      <Text style={{ color: colors.text.primary }} className="text-2xl font-bold text-center">
                        {capitalizeWords(profile.name)}
                      </Text>
                      <Text style={{ color: colors.text.secondary }} className="text-center mt-1">
                        {capitalizeWords(profile.tagline)}
                      </Text>
                      <View className="flex-row items-center gap-2 mt-3">
                        <View
                          className="px-3 py-1.5 rounded-full"
                          style={{ backgroundColor: colors.light[200] }}
                        >
                          <Text style={{ color: colors.text.primary }} className="text-sm font-medium">
                            {capitalizeWords(profile.industry)}
                          </Text>
                        </View>
                        <View
                          className="px-3 py-1.5 rounded-full"
                          style={{ backgroundColor: colors.primary[50] }}
                        >
                          <Text style={{ color: colors.primary[600] }} className="text-sm font-medium">
                            {stageLabels[profile.stage]}
                          </Text>
                        </View>
                      </View>
                    </>
                  )}
                </View>
              </View>
            </Animated.View>

            {/* Enhance Profile Section — shown when profile is incomplete */}
            {showEnhanceSection && !isEditing && (
              <Animated.View
                entering={FadeInUp.delay(240).duration(500)}
                className="mx-5 mb-6"
              >
                <View
                  className="rounded-2xl p-5"
                  style={{
                    backgroundColor: colors.surface.primary,
                    ...cardShadow,
                  }}
                >
                  {/* Header with completeness */}
                  <View className="flex-row items-center justify-between mb-4">
                    <View className="flex-row items-center gap-2">
                      <Sparkles size={18} color={colors.primary[600]} />
                      <Text style={{ color: colors.text.primary }} className="font-semibold">
                        Enhance your profile
                      </Text>
                    </View>
                    <View
                      className="px-2.5 py-1 rounded-full"
                      style={{ backgroundColor: colors.primary[50] }}
                    >
                      <Text style={{ color: colors.primary[600] }} className="text-xs font-bold">
                        {profileCompleteness}%
                      </Text>
                    </View>
                  </View>

                  {/* Progress bar */}
                  <View
                    className="h-1.5 rounded-full mb-4 overflow-hidden"
                    style={{ backgroundColor: colors.light[300] }}
                  >
                    <View
                      className="h-full rounded-full"
                      style={{
                        width: `${profileCompleteness}%`,
                        backgroundColor: colors.primary[600],
                      }}
                    />
                  </View>

                  {/* Enhancement cards */}
                  <View className="gap-2">
                    {enhanceCards.map((card) => (
                      <AnimatedPressable
                        key={card.id}
                        onPress={() =>
                          router.push({
                            pathname: card.route as any,
                            params: card.routeParams,
                          })
                        }
                        style={{
                          backgroundColor: card.done ? colors.surface.secondary : colors.surface.primary,
                          borderRadius: 12,
                          padding: 14,
                          borderWidth: 1,
                          borderColor: card.done ? colors.border.light : colors.border.medium,
                          opacity: card.done ? 0.7 : 1,
                        }}
                      >
                        <View className="flex-row items-center gap-3">
                          <View
                            className="w-10 h-10 rounded-xl items-center justify-center"
                            style={{
                              backgroundColor: card.done
                                ? colors.traffic.greenLight
                                : colors.primary[50],
                            }}
                          >
                            {card.done ? (
                              <CheckCircle size={20} color={colors.traffic.green} />
                            ) : (
                              card.icon
                            )}
                          </View>
                          <View className="flex-1">
                            <Text
                              style={{
                                color: card.done ? colors.text.tertiary : colors.text.primary,
                              }}
                              className="text-sm font-semibold"
                            >
                              {card.title}
                            </Text>
                            <Text
                              style={{ color: colors.text.tertiary }}
                              className="text-xs"
                            >
                              {card.description}
                            </Text>
                          </View>
                          {!card.done && (
                            <ChevronRight size={16} color={colors.text.tertiary} />
                          )}
                        </View>
                      </AnimatedPressable>
                    ))}
                  </View>
                </View>
              </Animated.View>
            )}

            {/* Company Description Section */}
            <Animated.View
              entering={FadeInUp.delay(250).duration(500)}
              className="mx-5 mb-6"
            >
              <View
                className="rounded-2xl p-5"
                style={{
                  backgroundColor: colors.surface.primary,
                  ...cardShadow,
                }}
              >
                <View className="flex-row items-center gap-2 mb-3">
                  <FileText size={18} color={colors.primary[600]} />
                  <Text style={{ color: colors.text.primary }} className="font-semibold">
                    About the Company
                  </Text>
                </View>
                {isEditing ? (
                  <TextInput
                    value={formData.description}
                    onChangeText={(v) => updateField('description', v)}
                    placeholder="Describe your company, what you do, and what makes you unique..."
                    placeholderTextColor={colors.text.tertiary}
                    multiline
                    numberOfLines={4}
                    className="rounded-xl px-4 py-3 min-h-[100px]"
                    style={{
                      ...editFieldStyle,
                      color: colors.text.primary,
                      textAlignVertical: 'top',
                    }}
                  />
                ) : (
                  <Text
                    style={{ color: profile?.description ? colors.text.secondary : colors.text.tertiary }}
                    className="text-sm leading-relaxed"
                  >
                    {profile?.description || 'No company description added yet. Tap Edit to add a description about your company.'}
                  </Text>
                )}
              </View>
            </Animated.View>

            {/* Strategic analysis — immediately after the complete About card
                and before the interview-digest prompt and Key Metrics. It sits
                inside the profile it describes; it is not a new tab. */}
            {strategicAnalysisEnabled && realStartupId && !isEditing && (
              <Animated.View entering={FadeInUp.delay(252).duration(500)} className="mx-5 mb-6">
                <StrategicAnalysisEntry
                  canEdit
                  {...strategicEntry}
                  onOpen={() => router.push(`/(startup)/strategic-analysis?startupId=${realStartupId}` as never)}
                  // A blocked founder goes straight to the focused form rather
                  // than to a report screen that cannot show them anything.
                  onCompleteInputs={() =>
                    router.push(`/(startup)/strategic-analysis?startupId=${realStartupId}&setup=1` as never)}
                />
              </Animated.View>
            )}

            {/* Re-open interview digest — review & publish non-sensitive fields
                later, without re-interviewing (Task 2.3). Shown only when a
                stored digest exists for this founder's real startup. */}
            {latestDigest && realStartupId && !isEditing && (
              <Animated.View
                entering={FadeInUp.delay(255).duration(500)}
                className="mx-5 mb-6"
              >
                <Pressable
                  onPress={() => setDigestReviewOpen(true)}
                  accessibilityRole="button"
                  accessibilityLabel="Review and publish your interview digest"
                  className="rounded-2xl p-5 flex-row items-center gap-4 active:opacity-80"
                  style={{ backgroundColor: colors.surface.primary, ...cardShadow }}
                >
                  <View
                    className="w-11 h-11 rounded-xl items-center justify-center"
                    style={{ backgroundColor: colors.primary[50] }}
                  >
                    <Sparkles size={22} color={colors.primary[600]} />
                  </View>
                  <View className="flex-1">
                    <Text
                      numberOfLines={1}
                      style={{ color: colors.text.primary }}
                      className="font-semibold"
                    >
                      Review your interview digest
                    </Text>
                    <Text
                      numberOfLines={2}
                      style={{ color: colors.text.tertiary }}
                      className="text-xs mt-0.5"
                    >
                      Publish the non-sensitive parts of your story to your public profile.
                    </Text>
                  </View>
                  <ChevronRight size={20} color={colors.text.tertiary} />
                </Pressable>
              </Animated.View>
            )}

            {/* Section Divider - Key Metrics */}
            <View className="px-5">
              <SectionDivider label="Key Metrics" marginTop={8} marginBottom={12} />
            </View>

            {/* Quick Stats */}
            <Animated.View
              entering={FadeInUp.delay(300).duration(500)}
              className="px-5 mb-6"
            >
              <View className="flex-row gap-3">
                <View
                  className="flex-1 rounded-2xl p-4 items-center"
                  style={{
                    backgroundColor: colors.surface.primary,
                    ...cardShadow,
                  }}
                >
                  <DollarSign size={24} color={colors.success} />
                  {isEditing ? (
                    <TextInput
                      value={formData.fundingTarget}
                      onChangeText={(v) => updateField('fundingTarget', v)}
                      placeholder="0"
                      keyboardType="numeric"
                      className="text-xl font-bold mt-2 text-center rounded-lg px-2 py-1 w-full"
                      style={{
                        ...editFieldStyle,
                        color: colors.text.primary,
                      }}
                    />
                  ) : (
                    <Text style={{ color: colors.text.primary }} className="text-xl font-bold mt-2">
                      {formatCurrency(profile.fundingTarget)}
                    </Text>
                  )}
                  <Text style={{ color: colors.text.tertiary }} className="text-xs">Target Raise</Text>
                </View>
                <View
                  className="flex-1 rounded-2xl p-4 items-center"
                  style={{
                    backgroundColor: colors.surface.primary,
                    ...cardShadow,
                  }}
                >
                  <Users size={24} color={colors.info} />
                  {isEditing ? (
                    <TextInput
                      value={formData.teamSize}
                      onChangeText={(v) => updateField('teamSize', v)}
                      placeholder="0"
                      keyboardType="numeric"
                      className="text-xl font-bold mt-2 text-center rounded-lg px-2 py-1 w-full"
                      style={{
                        ...editFieldStyle,
                        color: colors.text.primary,
                      }}
                    />
                  ) : (
                    <Text style={{ color: colors.text.primary }} className="text-xl font-bold mt-2">
                      {profile.teamSize}
                    </Text>
                  )}
                  <Text style={{ color: colors.text.tertiary }} className="text-xs">Team Members</Text>
                </View>
                <View
                  className="flex-1 rounded-2xl p-4 items-center"
                  style={{
                    backgroundColor: colors.surface.primary,
                    ...cardShadow,
                  }}
                >
                  <Calendar size={24} color={colors.warning} />
                  {isEditing ? (
                    <TextInput
                      value={formData.foundedYear}
                      onChangeText={(v) => updateField('foundedYear', v)}
                      placeholder="2024"
                      keyboardType="numeric"
                      className="text-xl font-bold mt-2 text-center rounded-lg px-2 py-1 w-full"
                      style={{
                        ...editFieldStyle,
                        color: colors.text.primary,
                      }}
                    />
                  ) : (
                    <Text style={{ color: colors.text.primary }} className="text-xl font-bold mt-2">
                      {profile.foundedYear}
                    </Text>
                  )}
                  <Text style={{ color: colors.text.tertiary }} className="text-xs">Founded</Text>
                </View>
              </View>
            </Animated.View>

            {/* Fundraising Round Section */}
            <View className="px-5">
              <SectionDivider label="Fundraising Round" icon={TrendingUp} marginTop={8} marginBottom={12} />

              <FundraisingSection
                isActivelyRaising={profile?.isActivelyRaising ?? false}
                onToggleRaising={() => {
                  if (profile) {
                    setStartupProfile({ ...profile, isActivelyRaising: !profile.isActivelyRaising });
                  }
                }}
                commitmentSummary={commitmentSummary}
                onCloseRound={handleCloseRound}
                commitmentSettings={commitmentSettings}
                onSetCommitmentSettings={setCommitmentSettings}
                externalCommitments={myExternalCommitments}
                showAddExternal={showAddExternal}
                onToggleAddExternal={() => { setEditingExternalId(null); setShowAddExternal(!showAddExternal); }}
                editingExternalId={editingExternalId}
                onSetEditingExternalId={setEditingExternalId}
                extName={extName}
                onSetExtName={setExtName}
                extFirm={extFirm}
                onSetExtFirm={setExtFirm}
                extAmount={extAmount}
                onSetExtAmount={setExtAmount}
                extType={extType}
                onSetExtType={setExtType}
                extNotes={extNotes}
                onSetExtNotes={setExtNotes}
                onAddExternal={handleAddExternal}
                onEditExternal={handleEditExternal}
                onRemoveExternal={removeExternalCommitment}
                verifiedCommitments={myVerifiedCommitments}
                fundraisingHistory={profile?.fundraisingHistory}
                onStartNewRound={() => {
                  if (profile) {
                    setStartupProfile({ ...profile, isActivelyRaising: true });
                  }
                }}
                formatDate={formatDate}
              />
            </View>

            {/* Section Divider - Details */}
            <View className="px-5">
              <SectionDivider label="Details" marginTop={8} marginBottom={12} />
            </View>

            {/* Details */}
            <Animated.View
              entering={FadeInUp.delay(400).duration(500)}
              className="px-5 mb-6"
            >
              <View
                className="rounded-2xl overflow-hidden"
                style={{
                  backgroundColor: colors.surface.primary,
                  ...cardShadow,
                }}
              >
                {/* Location */}
                <View
                  className="flex-row items-center gap-4 p-4 border-b"
                  style={{ borderColor: colors.border.light }}
                >
                  <View
                    className="w-10 h-10 rounded-xl items-center justify-center"
                    style={{ backgroundColor: colors.primary[50] }}
                  >
                    <MapPin size={20} color={colors.info} />
                  </View>
                  <View className="flex-1">
                    <Text style={{ color: colors.text.tertiary }} className="text-xs">Location</Text>
                    {isEditing ? (
                      <TextInput
                        value={formData.location}
                        onChangeText={(v) => updateField('location', v)}
                        placeholder="City, Country"
                        className="font-medium rounded-lg px-2 py-1 mt-1"
                        style={{
                          backgroundColor: colors.surface.secondary,
                          color: colors.text.primary,
                          borderWidth: 1,
                          borderColor: colors.primary[200],
                        }}
                      />
                    ) : (
                      <Text style={{ color: colors.text.primary }} className="font-medium">{capitalizeWords(profile.location)}</Text>
                    )}
                  </View>
                </View>

                {/* Website */}
                <View
                  className="flex-row items-center gap-4 p-4 border-b"
                  style={{ borderColor: colors.border.light }}
                >
                  <View
                    className="w-10 h-10 rounded-xl items-center justify-center"
                    style={{ backgroundColor: colors.primary[50] }}
                  >
                    <Globe size={20} color={colors.primary[600]} />
                  </View>
                  <View className="flex-1">
                    <Text style={{ color: colors.text.tertiary }} className="text-xs">Website</Text>
                    {isEditing ? (
                      <TextInput
                        value={formData.website}
                        onChangeText={(v) => updateField('website', v)}
                        placeholder="www.example.com"
                        autoCapitalize="none"
                        className="font-medium rounded-lg px-2 py-1 mt-1"
                        style={{
                          backgroundColor: colors.surface.secondary,
                          color: colors.text.primary,
                          borderWidth: 1,
                          borderColor: colors.primary[200],
                        }}
                      />
                    ) : (
                      <Text style={{ color: colors.text.primary }} className="font-medium">
                        {profile.website || 'Not set'}
                      </Text>
                    )}
                  </View>
                </View>

                {/* LinkedIn */}
                <View
                  className="flex-row items-center gap-4 p-4 border-b"
                  style={{ borderColor: colors.border.light }}
                >
                  <View
                    className="w-10 h-10 rounded-xl items-center justify-center"
                    style={{ backgroundColor: colors.primary[50] }}
                  >
                    <Linkedin size={20} color={colors.primary[600]} />
                  </View>
                  <View className="flex-1">
                    <Text style={{ color: colors.text.tertiary }} className="text-xs">LinkedIn</Text>
                    {isEditing ? (
                      <TextInput
                        value={formData.linkedIn}
                        onChangeText={(v) => updateField('linkedIn', v)}
                        placeholder="linkedin.com/company/..."
                        autoCapitalize="none"
                        className="font-medium rounded-lg px-2 py-1 mt-1"
                        style={{
                          backgroundColor: colors.surface.secondary,
                          color: colors.text.primary,
                          borderWidth: 1,
                          borderColor: colors.primary[200],
                        }}
                      />
                    ) : (
                      <Text style={{ color: colors.text.primary }} className="font-medium">
                        {profile.linkedIn || 'Not set'}
                      </Text>
                    )}
                  </View>
                </View>

                {/* Minimum Ticket */}
                <View className="flex-row items-center gap-4 p-4">
                  <View
                    className="w-10 h-10 rounded-xl items-center justify-center"
                    style={{ backgroundColor: colors.traffic.greenLight }}
                  >
                    <DollarSign size={20} color={colors.success} />
                  </View>
                  <View className="flex-1">
                    <Text style={{ color: colors.text.tertiary }} className="text-xs">Minimum Ticket</Text>
                    {isEditing ? (
                      <TextInput
                        value={formData.minimumTicket}
                        onChangeText={(v) => updateField('minimumTicket', v)}
                        placeholder="25000"
                        keyboardType="numeric"
                        className="font-medium rounded-lg px-2 py-1 mt-1"
                        style={{
                          backgroundColor: colors.surface.secondary,
                          color: colors.text.primary,
                          borderWidth: 1,
                          borderColor: colors.primary[200],
                        }}
                      />
                    ) : (
                      <Text style={{ color: colors.text.primary }} className="font-medium">
                        {profile.minimumTicket ? formatCurrency(profile.minimumTicket) : 'Not set'}
                      </Text>
                    )}
                  </View>
                </View>
              </View>
            </Animated.View>

            {/* Section Divider - About */}
            <View className="px-5">
              <SectionDivider label="About" marginTop={8} marginBottom={12} />
            </View>

            {/* Company Description */}
            <Animated.View
              entering={FadeInUp.delay(450).duration(500)}
              className="px-5 mb-6"
            >
              <View
                className="rounded-2xl p-5"
                style={{
                  backgroundColor: colors.surface.primary,
                  ...cardShadow,
                }}
              >
                {/* Problem */}
                <View className="mb-4">
                  <View className="flex-row items-center gap-2 mb-2">
                    <AlertCircle size={16} color={colors.warning} />
                    <Text
                      style={{ color: colors.text.secondary }}
                      className="text-xs font-semibold uppercase tracking-wide"
                    >
                      The Problem
                    </Text>
                  </View>
                  {isEditing ? (
                    <TextInput
                      value={formData.problem}
                      onChangeText={(v) => updateField('problem', v)}
                      placeholder="What problem are you solving?"
                      multiline
                      className="text-sm leading-relaxed rounded-lg px-3 py-2 min-h-[80px]"
                      style={{
                        backgroundColor: colors.surface.secondary,
                        color: colors.text.secondary,
                        borderWidth: 1,
                        borderColor: colors.primary[200],
                        textAlignVertical: 'top',
                      }}
                    />
                  ) : (
                    <Text
                      style={{ color: colors.text.secondary }}
                      className="text-sm leading-relaxed"
                    >
                      {profile.problem || (mode.showSampleData ? COMPANY_DESCRIPTION.problem : '')}
                    </Text>
                  )}
                </View>

                {/* Solution */}
                <View className="mb-4">
                  <View className="flex-row items-center gap-2 mb-2">
                    <Sparkles size={16} color={colors.success} />
                    <Text
                      style={{ color: colors.text.secondary }}
                      className="text-xs font-semibold uppercase tracking-wide"
                    >
                      Our Solution
                    </Text>
                  </View>
                  {isEditing ? (
                    <TextInput
                      value={formData.solution}
                      onChangeText={(v) => updateField('solution', v)}
                      placeholder="How do you solve it?"
                      multiline
                      className="text-sm leading-relaxed rounded-lg px-3 py-2 min-h-[80px]"
                      style={{
                        backgroundColor: colors.surface.secondary,
                        color: colors.text.secondary,
                        borderWidth: 1,
                        borderColor: colors.primary[200],
                        textAlignVertical: 'top',
                      }}
                    />
                  ) : (
                    <Text
                      style={{ color: colors.text.secondary }}
                      className="text-sm leading-relaxed"
                    >
                      {profile.solution || (mode.showSampleData ? COMPANY_DESCRIPTION.solution : '')}
                    </Text>
                  )}
                </View>

                {/* Target Customer */}
                <View>
                  <View className="flex-row items-center gap-2 mb-2">
                    <Target size={16} color={colors.primary[600]} />
                    <Text
                      style={{ color: colors.text.secondary }}
                      className="text-xs font-semibold uppercase tracking-wide"
                    >
                      Target Customer
                    </Text>
                  </View>
                  {isEditing ? (
                    <TextInput
                      value={formData.targetCustomer}
                      onChangeText={(v) => updateField('targetCustomer', v)}
                      placeholder="Who is your ideal customer?"
                      multiline
                      className="text-sm leading-relaxed rounded-lg px-3 py-2 min-h-[60px]"
                      style={{
                        backgroundColor: colors.surface.secondary,
                        color: colors.text.secondary,
                        borderWidth: 1,
                        borderColor: colors.primary[200],
                        textAlignVertical: 'top',
                      }}
                    />
                  ) : (
                    <Text
                      style={{ color: colors.text.secondary }}
                      className="text-sm leading-relaxed"
                    >
                      {profile.targetCustomer || 'Not defined'}
                    </Text>
                  )}
                </View>
              </View>
            </Animated.View>

            {/* Only show AI Insights, Verification, Updates, and Account sections when NOT editing */}
            {!isEditing && (
              <>
                {/* Section Divider - AI Insights (demo/mock mode only) */}
                {mode.showSampleData && (
                  <>
                    <View className="px-5">
                      <SectionDivider label="Recommendations" marginTop={8} marginBottom={12} />
                    </View>

                    {/* AI-Extracted Data Room Insights */}
                    <Animated.View
                      entering={FadeInUp.delay(500).duration(500)}
                      className="px-5 mb-6"
                    >
                      <View className="flex-row items-center justify-between mb-4">
                        <View className="flex-row items-center gap-2">
                          <Sparkles size={20} color={colors.primary[600]} />
                          <Text style={{ color: colors.text.primary }} className="text-lg font-semibold">From Data Room</Text>
                        </View>
                      </View>
                      <View
                        className="rounded-2xl overflow-hidden"
                        style={{
                          backgroundColor: colors.surface.primary,
                          ...cardShadow,
                        }}
                      >
                        {AI_INSIGHTS.map((insight, index) => (
                          <View
                            key={insight.id}
                            className={`p-4 ${index < AI_INSIGHTS.length - 1 ? 'border-b' : ''}`}
                            style={{ borderColor: colors.border.light }}
                          >
                            <View className="flex-row items-start gap-3">
                              <View
                                className="w-8 h-8 rounded-lg items-center justify-center mt-0.5"
                                style={{
                                  backgroundColor: insight.category === 'Risk Factor'
                                    ? colors.amber.light
                                    : colors.primary[50],
                                }}
                              >
                                {insight.category === 'Risk Factor' ? (
                                  <AlertCircle size={16} color={colors.warning} />
                                ) : insight.category === 'Financial Metrics' ? (
                                  <TrendingUp size={16} color={colors.primary[600]} />
                                ) : (
                                  <FileText size={16} color={colors.primary[600]} />
                                )}
                              </View>
                              <View className="flex-1">
                                <Text
                                  style={{
                                    color: insight.category === 'Risk Factor'
                                      ? colors.warning
                                      : colors.primary[600],
                                  }}
                                  className="text-xs font-semibold uppercase tracking-wide mb-1"
                                >
                                  {insight.category}
                                </Text>
                                <Text
                                  style={{ color: colors.text.primary }}
                                  className="text-sm leading-relaxed mb-2"
                                >
                                  {insight.insight}
                                </Text>
                                <Pressable className="flex-row items-center gap-1.5">
                                  <ExternalLink size={12} color={colors.text.tertiary} />
                                  <Text style={{ color: colors.text.tertiary }} className="text-xs">
                                    {insight.source}
                                    {insight.sourcePage && ` (p. ${insight.sourcePage})`}
                                  </Text>
                                </Pressable>
                              </View>
                            </View>
                          </View>
                        ))}
                      </View>
                      <Pressable
                        onPress={() => router.push('/(startup)/dataroom')}
                        className="flex-row items-center justify-center gap-2 mt-3 py-2"
                      >
                        <Text style={{ color: colors.primary[600] }} className="text-sm font-medium">
                          View Data Room
                        </Text>
                        <ChevronRight size={16} color={colors.primary[600]} />
                      </Pressable>
                    </Animated.View>
                  </>
                )}

                {/* Section Divider - Verification Requests */}
                <View className="px-5">
                  <SectionDivider label="Verification Requests" marginTop={8} marginBottom={12} />
                </View>

                {/* Verification Requests Section */}
                <Animated.View
                  entering={FadeInUp.delay(550).duration(500)}
                  className="px-5 mb-6"
                >
                  <View
                    className="rounded-2xl overflow-hidden"
                    style={{
                      backgroundColor: colors.surface.primary,
                      ...cardShadow,
                    }}
                  >
                    {pendingRequests.length > 0 ? (
                      <>
                        <View className="p-4 border-b" style={{ borderColor: colors.border.light }}>
                          <View className="flex-row items-center gap-2">
                            <View
                              className="w-8 h-8 rounded-full items-center justify-center"
                              style={{ backgroundColor: colors.amber.light }}
                            >
                              <AlertCircle size={16} color={colors.amber.main} />
                            </View>
                            <Text style={{ color: colors.text.primary }} className="font-semibold">
                              {pendingRequests.length} Pending Request{pendingRequests.length > 1 ? 's' : ''}
                            </Text>
                          </View>
                        </View>
                        {pendingRequests.slice(0, 2).map((request, index) => (
                          <View
                            key={request.id}
                            className={`p-4 ${index < Math.min(pendingRequests.length, 2) - 1 ? 'border-b' : ''}`}
                            style={{ borderColor: colors.border.light }}
                          >
                            <View className="flex-row items-center mb-3">
                              <View
                                className="w-10 h-10 rounded-full items-center justify-center mr-3"
                                style={{ backgroundColor: colors.primary[50] }}
                              >
                                <Users size={18} color={colors.primary[600]} />
                              </View>
                              <View className="flex-1">
                                <Text style={{ color: colors.text.primary }} className="font-medium">
                                  {capitalizeWords(request.investorName)}
                                </Text>
                                {request.investorFirm && (
                                  <Text style={{ color: colors.text.tertiary }} className="text-xs">
                                    {capitalizeWords(request.investorFirm)}
                                  </Text>
                                )}
                              </View>
                            </View>
                            <View className="flex-row gap-2">
                              <Pressable
                                onPress={() => handleReject(request.id)}
                                className="flex-1 flex-row items-center justify-center gap-1.5 py-2.5 rounded-lg"
                                style={{ backgroundColor: colors.traffic.redLight }}
                              >
                                <XCircle size={14} color={colors.traffic.red} />
                                <Text style={{ color: colors.traffic.red }} className="text-sm font-medium">
                                  Reject
                                </Text>
                              </Pressable>
                              <Pressable
                                onPress={() => handleVerify(request.id)}
                                className="flex-1 flex-row items-center justify-center gap-1.5 py-2.5 rounded-lg"
                                style={{ backgroundColor: colors.traffic.green }}
                              >
                                <CheckCircle size={14} color="white" />
                                <Text className="text-sm font-medium text-white">Verify</Text>
                              </Pressable>
                            </View>
                          </View>
                        ))}
                      </>
                    ) : (
                      <View className="p-6 items-center">
                        <View
                          className="w-12 h-12 rounded-full items-center justify-center mb-3"
                          style={{ backgroundColor: colors.traffic.greenLight }}
                        >
                          <Shield size={24} color={colors.traffic.green} />
                        </View>
                        <Text style={{ color: colors.text.primary }} className="font-semibold text-center mb-1">
                          No Pending Requests
                        </Text>
                        <Text style={{ color: colors.text.secondary }} className="text-sm text-center">
                          Investor verification requests will appear here
                        </Text>
                      </View>
                    )}
                  </View>
                </Animated.View>

                {/* Section Divider - News & Updates */}
                <View className="px-5">
                  <SectionDivider label="News & Updates" icon={Newspaper} marginTop={8} marginBottom={12} />
                </View>

                {/* Recent Updates - Collapsible */}
                <Animated.View
                  entering={FadeInUp.delay(575).duration(500)}
                  className="px-5 mb-6"
                >
                  <RecentUpdates updates={updates} />
                </Animated.View>

                {/* Section Divider - Account */}
                <View className="px-5">
                  <SectionDivider label="Account" marginTop={8} marginBottom={12} />
                </View>

                {/* Actions */}
                <Animated.View
                  entering={FadeInUp.delay(600).duration(500)}
                  className="px-5 pb-8"
                >
                  <View
                    className="rounded-2xl overflow-hidden"
                    style={{
                      backgroundColor: colors.surface.primary,
                      ...cardShadow,
                    }}
                  >
                    <Pressable
                      onPress={() => router.push('/startup-team')}
                      accessibilityRole="button"
                      accessibilityLabel="Team management"
                      className="flex-row items-center justify-between p-4 border-b active:opacity-70"
                      style={{ borderColor: colors.border.light }}
                    >
                      <View className="flex-row items-center gap-4">
                        <View
                          className="w-10 h-10 rounded-xl items-center justify-center"
                          style={{ backgroundColor: colors.primary[50] }}
                        >
                          <UserPlus size={20} color={colors.info} />
                        </View>
                        <View>
                          <Text style={{ color: colors.text.primary }} className="font-medium">Team Management</Text>
                          <Text style={{ color: colors.text.tertiary }} className="text-xs">
                            {teamMembers.length > 0 ? `${teamMembers.length + 1} members` : 'Add team members'}
                          </Text>
                        </View>
                      </View>
                      <ChevronRight size={20} color={colors.text.tertiary} />
                    </Pressable>

                    {COMPANY_PLANS_LINK && (
                      <Pressable
                        onPress={() => COMPANY_PLANS_LINK!.open(router, 'startup')}
                        accessibilityRole="button"
                        accessibilityLabel={COMPANY_PLANS_LINK.label}
                        className="flex-row items-center justify-between p-4 border-b active:opacity-70"
                        style={{ borderColor: colors.border.light }}
                      >
                        <View className="flex-row items-center gap-4">
                          <View
                            className="w-10 h-10 rounded-xl items-center justify-center"
                            style={{ backgroundColor: colors.primary[50] }}
                          >
                            <Crown size={20} color={colors.primary[600]} />
                          </View>
                          <View>
                            <Text numberOfLines={1} style={{ color: colors.text.primary }} className="font-medium">{COMPANY_PLANS_LINK.label}</Text>
                            <Text style={{ color: colors.text.tertiary }} className="text-xs">Free Plan</Text>
                          </View>
                        </View>
                        <ChevronRight size={20} color={colors.text.tertiary} />
                      </Pressable>
                    )}

                    <Pressable
                      onPress={() => router.push('/settings')}
                      accessibilityRole="button"
                      accessibilityLabel="Settings"
                      className="flex-row items-center justify-between p-4 border-b active:opacity-70"
                      style={{ borderColor: colors.border.light }}
                    >
                      <View className="flex-row items-center gap-4">
                        <View
                          className="w-10 h-10 rounded-xl items-center justify-center"
                          style={{ backgroundColor: colors.light[200] }}
                        >
                          <Settings size={20} color={colors.text.secondary} />
                        </View>
                        <Text style={{ color: colors.text.primary }} className="font-medium">Settings</Text>
                      </View>
                      <ChevronRight size={20} color={colors.text.tertiary} />
                    </Pressable>

                    <Pressable
                      onPress={handleLogout}
                      accessibilityRole="button"
                      accessibilityLabel="Sign out"
                      className="flex-row items-center justify-between p-4 active:opacity-70"
                    >
                      <View className="flex-row items-center gap-4">
                        <View
                          className="w-10 h-10 rounded-xl items-center justify-center"
                          style={{ backgroundColor: colors.red.light }}
                        >
                          <LogOut size={20} color={colors.error} />
                        </View>
                        <Text style={{ color: colors.error }} className="font-medium">Sign Out</Text>
                      </View>
                      <ChevronRight size={20} color={colors.text.tertiary} />
                    </Pressable>
                  </View>
                </Animated.View>
              </>
            )}

            {/* Save button at bottom when editing (mobile) */}
            {isEditing && !isWeb && (
              <View className="px-5 pb-8">
                <Pressable
                  onPress={saveChanges}
                  disabled={isSaving}
                  className="flex-row items-center justify-center gap-2 py-4 rounded-xl active:opacity-80"
                  style={{ backgroundColor: colors.primary[600] }}
                >
                  <Save size={20} color="white" />
                  <Text className="font-semibold text-white">
                    {isSaving ? 'Saving...' : 'Save Changes'}
                  </Text>
                </Pressable>
              </View>
            )}
          </KeyboardAwareBody>

        </SafeAreaView>

        {/* Digest review overlay — re-opened from the entry point above. Publishes
            only toggled-on allowlisted fields; on close it refreshes the profile.
            Rescore is NOT re-run here (the session already completed) — onConfirmed
            simply closes and re-reads the stored digest. */}
        {digestReviewOpen && latestDigest && realStartupId && (
          <View
            className="absolute inset-0"
            style={{ backgroundColor: colors.surface.primary }}
          >
            <SafeAreaView edges={isWeb ? ['bottom'] : ['top', 'bottom']} className="flex-1">
              <InterviewDigestReview
                sessionId={latestDigest.sessionId}
                startupId={realStartupId}
                digest={latestDigest.digest}
                onConfirmed={() => setDigestReviewOpen(false)}
                onSkip={() => setDigestReviewOpen(false)}
                confirmLabel="Publish updates"
                skipLabel="Close"
              />
            </SafeAreaView>
          </View>
        )}
      </View>
    </AppLayout>
  );
}
